import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import moment, { Moment } from 'moment'

import { isDemoPage } from '../../../../../utils/hooks'
import {
  DEMO_DELIVERY_DATE_CHOOSE_EVENT,
  DEMO_DELIVERY_DATE_OPEN_EVENT,
  DEMO_DELIVERY_SET_FIELD_EVENT,
  DEMO_DELIVERY_LOCATION_CHOOSE_EVENT,
  DEMO_DELIVERY_LOCATION_SEARCH_EVENT,
} from '../../playback-constants'

type DemoDeliveryFormValues = {
  location_from?: string
  location_to?: string
  planned_shipment_date?: string
}

export const useDemoDelivery = (
  route: Route | undefined,
  isCompleted: boolean | undefined,
  defaultValues: DemoDeliveryFormValues,
) => {
  const isDemoMode = isDemoPage()
  const [ shouldShowSearchForm, setShouldShowSearchForm ] = useState(isDemoMode)
  const formValues = { ...defaultValues }

  if (isDemoMode) {
    formValues.location_from = undefined
    formValues.location_to = undefined
    formValues.planned_shipment_date = undefined
  }

  const shouldShowCompleted = Boolean(route?.id && isCompleted) && !shouldShowSearchForm

  return {
    choiceProps: {
      'data-demo-playback-target': isDemoMode ? 'delivery-choice' : undefined,
    },
    complete: () => setShouldShowSearchForm(false),
    demoRoute: isDemoMode ? route : undefined,
    findButtonProps: {
      'data-demo-playback-action': isDemoMode ? 'find-delivery' : undefined,
    },
    formValues,
    shouldShowCompleted,
  }
}

type UseDemoDeliverySearchFormParams = {
  demoRoute?: Route
  setLocationFrom(value: string): void
  setLocationTo(value: string): void
  setValue(name: string, value: string, options: Record<string, boolean>): void
}

export const useDemoDeliverySearchForm = ({
  demoRoute,
  setLocationFrom,
  setLocationTo,
  setValue,
}: UseDemoDeliverySearchFormParams) => {
  const isDemoMode = isDemoPage() && Boolean(demoRoute)

  useEffect(() => {
    if (!demoRoute) {
      return
    }

    const handleSetField = (event: Event) => {
      const { fieldName, value } = (event as CustomEvent<{
        fieldName: string
        value: string
      }>).detail

      setValue(fieldName, value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: fieldName !== 'planned_shipment_date',
      })

      if (fieldName === 'location_from') {
        setLocationFrom(value)
      }

      if (fieldName === 'location_to') {
        setLocationTo(value)
      }
    }

    window.addEventListener(DEMO_DELIVERY_SET_FIELD_EVENT, handleSetField)

    return () => window.removeEventListener(DEMO_DELIVERY_SET_FIELD_EVENT, handleSetField)
  }, [ demoRoute, setLocationFrom, setLocationTo, setValue ])

  return {
    buttonProps: {
      'data-demo-allowed': isDemoMode ? 'delivery-search' : undefined,
      'data-demo-playback-action': isDemoMode ? 'search-delivery' : undefined,
    },
    formProps: {
      'data-demo-delivery-search': demoRoute ? 'true' : undefined,
      'data-demo-location-from-id': demoRoute?.location_from.id,
      'data-demo-location-from-name': demoRoute?.location_from.name,
      'data-demo-location-to-id': demoRoute?.location_to.id,
      'data-demo-location-to-name': demoRoute?.location_to.name,
      'data-demo-planned-shipment-date': demoRoute?.planned_shipment_date,
    },
  }
}

export const getDemoDeliveryRouteButtonProps = (isActive?: boolean) => {
  const isDemoRoute = isDemoPage() && !isActive

  return {
    'data-demo-allowed': isDemoRoute ? 'delivery-route' : undefined,
    'data-demo-playback-action': isDemoRoute ? 'choose-delivery-route' : undefined,
  }
}

export const getDemoVisibleDeliveryRoutes = (routes: Route[], currentRouteId?: number) => {
  if (isDemoPage()) {
    return routes
  }

  return routes.filter(route => route.id !== currentRouteId)
}

export const shouldShowDemoCurrentRoute = (currentRouteId?: number) => {
  return !isDemoPage() && Boolean(currentRouteId)
}

export const getDemoDeliveryCostRange = (
  currentRoute: Route | undefined,
  minTotal: number,
  maxTotal: number,
) => {
  if (!isDemoPage() || !currentRoute) {
    return [ minTotal, maxTotal ] as [number, number]
  }

  return [ 0, currentRoute.total_price ] as [number, number]
}

type UseDemoDeliveryDateParams = {
  demoDate?: string
  setDate: Dispatch<SetStateAction<Moment | null>>
  setValue(name: string, value: string, options: Record<string, boolean>): void
}

export const useDemoDeliveryDate = ({
  demoDate,
  setDate,
  setValue,
}: UseDemoDeliveryDateParams) => {
  const isDemoMode = isDemoPage() && Boolean(demoDate)
  const [ isOpen, setIsOpen ] = useState(false)

  useEffect(() => {
    if (!isDemoMode || !demoDate) {
      return
    }

    const handleOpen = () => {
      setDate(moment(demoDate))
      setIsOpen(true)
    }

    const handleChoose = () => {
      setDate(moment(demoDate))
      setValue('planned_shipment_date', demoDate, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      })
      setIsOpen(false)
    }

    window.addEventListener(DEMO_DELIVERY_DATE_OPEN_EVENT, handleOpen)
    window.addEventListener(DEMO_DELIVERY_DATE_CHOOSE_EVENT, handleChoose)

    return () => {
      window.removeEventListener(DEMO_DELIVERY_DATE_OPEN_EVENT, handleOpen)
      window.removeEventListener(DEMO_DELIVERY_DATE_CHOOSE_EVENT, handleChoose)
    }
  }, [ demoDate, isDemoMode, setDate, setValue ])

  return {
    disablePast: !isDemoMode,
    onClose: isDemoMode ? () => setIsOpen(false) : undefined,
    open: isDemoMode ? isOpen : undefined,
  }
}

type DemoLocation = Pick<LocationItem, 'id' | 'name'>

type UseDemoDeliveryLocationParams = {
  demoValue?: DemoLocation
  fieldName: string
  fromFieldName: string
  getFromValue(): string | undefined
  inputRef: RefObject<HTMLInputElement>
  locations: LocationItem[]
  locationRequest(params: { deps_id?: string; q: string }): any
  onChoose(location: LocationItem): void
  onSearchComplete(): void
}

export const useDemoDeliveryLocation = ({
  demoValue,
  fieldName,
  fromFieldName,
  getFromValue,
  inputRef,
  locations,
  locationRequest,
  onChoose,
  onSearchComplete,
}: UseDemoDeliveryLocationParams) => {
  const isDemoMode = isDemoPage()
  const [ inputValue, setInputValue ] = useState('')
  const [ isOpen, setIsOpen ] = useState(false)
  const hasDemoValue = isDemoMode && Boolean(demoValue)

  useEffect(() => {
    if (!hasDemoValue || !demoValue) {
      return
    }

    const locationField = inputRef.current?.closest<HTMLElement>('.searchForm__city')

    const handleSearch = (event: Event) => {
      const detail = (event as CustomEvent<{
        fieldName: string
        query: string
        shouldOpen: boolean
      }>).detail

      if (detail?.fieldName !== fieldName) {
        return
      }

      const query = detail.query || ''
      const minimumQueryLength = Math.min(3, demoValue.name.length)
      const queryIsComplete = query.length >= minimumQueryLength
      const depsId = fieldName === fromFieldName ? undefined : getFromValue()

      inputRef.current?.focus({ preventScroll: true })
      setInputValue(query)
      setIsOpen(detail.shouldOpen && queryIsComplete)

      if (detail.shouldOpen && queryIsComplete) {
        locationRequest({ deps_id: depsId, q: query }).then(onSearchComplete)
      }
    }

    const handleChoose = (event: Event) => {
      const selectedFieldName = (event as CustomEvent<{ fieldName: string }>).detail?.fieldName

      if (selectedFieldName !== fieldName) {
        return
      }

      const targetLocation = locations.find(location => location.id === demoValue.id)

      if (!targetLocation) {
        return
      }

      onChoose(targetLocation)
      setInputValue(targetLocation.name)
      setIsOpen(false)
      inputRef.current?.blur()
    }

    window.addEventListener(DEMO_DELIVERY_LOCATION_SEARCH_EVENT, handleSearch)
    window.addEventListener(DEMO_DELIVERY_LOCATION_CHOOSE_EVENT, handleChoose)
    locationField?.setAttribute('data-demo-playback-ready', 'true')

    return () => {
      window.removeEventListener(DEMO_DELIVERY_LOCATION_SEARCH_EVENT, handleSearch)
      window.removeEventListener(DEMO_DELIVERY_LOCATION_CHOOSE_EVENT, handleChoose)
      locationField?.removeAttribute('data-demo-playback-ready')
    }
  }, [
    demoValue,
    fieldName,
    fromFieldName,
    getFromValue,
    hasDemoValue,
    inputRef,
    locationRequest,
    locations,
    onChoose,
    onSearchComplete,
  ])

  const getOptionFieldName = (locationId: string) => {
    if (!hasDemoValue || demoValue?.id !== locationId) {
      return undefined
    }

    return fieldName
  }

  return {
    autocompleteProps: {
      inputValue: hasDemoValue ? inputValue : undefined,
      onClose: hasDemoValue ? () => setIsOpen(false) : undefined,
      open: hasDemoValue ? isOpen : undefined,
    },
    fieldName: hasDemoValue ? fieldName : undefined,
    getOptionFieldName,
  }
}
